import { useState, useEffect } from 'react';
import { db } from '../config/firebase';
import {
  collection,
  doc,
  onSnapshot,
  query,
  where,
  QueryConstraint,
  DocumentSnapshot,
  FirestoreError,
} from 'firebase/firestore';
import { CategoryKey, Listing } from '../types';

/** All directory listings, shuls included, live here (docs/directory-plan.md section 3). */
export const LISTINGS_COLLECTION = 'listings';

export function listingFromSnapshot(snapshot: DocumentSnapshot): Listing {
  return { id: snapshot.id, ...snapshot.data() } as Listing;
}

/** True when Firestore refused the read (e.g. a hidden listing the user may not see). */
export function isPermissionDenied(err: unknown): boolean {
  return (err as FirestoreError | undefined)?.code === 'permission-denied';
}

export interface UseListingsOptions {
  /** Only this category. */
  category?: CategoryKey;
  /**
   * Super admin only: also load pending, draft and hidden listings.
   * Everyone else gets published listings only (the rules refuse anything else).
   */
  includeUnpublished?: boolean;
  /** Don't query yet (e.g. while the user is still loading). */
  skip?: boolean;
}

/**
 * Listings, live-updating. Not sorted: sort in the browser so no extra
 * Firestore indexes are needed (docs/phase-1-plan.md technical notes).
 */
export function useListings(options: UseListingsOptions = {}) {
  const { category, includeUnpublished = false, skip = false } = options;
  const [listings, setListings] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(!skip);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (skip) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const constraints: QueryConstraint[] = [];
    if (!includeUnpublished) constraints.push(where('status', '==', 'published'));
    if (category) constraints.push(where('category', '==', category));
    const unsubscribe = onSnapshot(
      query(collection(db, LISTINGS_COLLECTION), ...constraints),
      (snapshot) => {
        setListings(snapshot.docs.map(listingFromSnapshot));
        setError(null);
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, [category, includeUnpublished, skip]);

  return { listings, loading, error };
}

/**
 * One listing, live-updating. `listing` is null when it doesn't exist or the
 * user may not see it (e.g. hidden), so pages can show "not found".
 */
export function useListing(listingId: string) {
  const [listing, setListing] = useState<Listing | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!listingId) {
      setListing(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const unsubscribe = onSnapshot(
      doc(db, LISTINGS_COLLECTION, listingId),
      (snapshot) => {
        setListing(snapshot.exists() ? listingFromSnapshot(snapshot) : null);
        setError(null);
        setLoading(false);
      },
      (err) => {
        setListing(null);
        if (!isPermissionDenied(err)) setError(err.message);
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, [listingId]);

  return { listing, loading, error };
}

/** Every listing this user owns, whatever its status. */
export function useOwnerListings(uid: string | undefined) {
  const [listings, setListings] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!uid) {
      setListings([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const unsubscribe = onSnapshot(
      query(collection(db, LISTINGS_COLLECTION), where('ownerUids', 'array-contains', uid)),
      (snapshot) => {
        setListings(snapshot.docs.map(listingFromSnapshot));
        setError(null);
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, [uid]);

  return { listings, loading, error };
}
